/* eslint-disable @nx/enforce-module-boundaries */
// import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ANALYTICS_EVENTS, trackEvent } from 'v4/utils/analyticsEvents';
import { useDispatch, useSelector } from 'react-redux';
import { newPost, quotePost, updatePost } from 'v4/store/actions/post.actions';
import { useDisclosure } from '@mybridge/ui/hooks';
import { getAllCompanyPages } from 'v4/store/actions/companyPages.actions';
import { EditorState, CompositeDecorator, ContentState } from 'draft-js';
import { useToast } from '@mybridge/ui/hooks';
import { Box, CloseButton, HStack, Text } from '@mybridge/ui';
import { getTagUsersConnectionsData } from 'v4/store/actions/connection.actions';
import { useCurrentLocation } from 'v4/lib/hooks/use-location';
import { MyProfileContext } from '../profile/context';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import io from 'socket.io-client';
import { useChatSocket } from 'packages/mybridgev4/hooks/useChatSocket';
import { useMultipartUpload } from 'packages/mybridgev4/hooks/useMultipartUpload';

const SOCKET_SERVER_URL = process.env.SOCKET_URL;
const TOKEN_NAME = process.env.BASE_TOKEN_NAME;

export const PostFormContext = createContext({});

export const usePostFormContext = ({
  post,
  refetchAll,
  onCreateSuccess,
  onUpdateSuccess,
  fromProfilePhotoVideo,
  isPostModalOpen = true,
}) => {
  const [mode, setMode] = useState('create'); // create | edit
  const toast = useToast();
  const [multiPost, setMultiPost] = useState([]);
  const [isRemoved, setIsRemoved] = useState(false);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [videoObject, setVideoObject] = useState([]);
  const [imageObject, setImageObject] = useState([]);
  const [gifsObject, setGifsObject] = useState([]);
  const [tagged, setTagged] = useState([]);
  const [visibility, setVisibility] = useState(0);
  const [replyControl, setReplyControl] = useState(0);
  const [companyId, setCompanyId] = useState(null);
  const [isDisable, setIsDisable] = useState(false);
  const [gifs, setGifs] = useState([]);
  const [moreOptionsActiveTab, setMoreOptionsActiveTab] = useState(null);
  const [feeling, setFeeling] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [closeModal, setCloseModal] = useState(false);
  const [mediaUpdated, setMediaUpdated] = useState(false);
  const [publishingData, setPublishingData] = useState(false);
  const [multiCreateLoading, setMultiCreateLoading] = useState(false);
  const [isCompany, setIsCompany] = useState(false);
  const [thumbnailData, setThumbnailData] = useState(null);
  const [text, setText] = useState('');
  const [isReactionMenuOpen, setIsReactionMenuOpen] = useState(true);
  const [isControlMenuOpen, setIsControlMenuOpen] = useState(false);
  const [openMoreOptions, setOpenMoreOptions] = useState(false);
  const [emojis, setEmojis] = useState([]);
  const [caretPosition, setCaretPosition] = useState(null);
  const [mentionedUser, setMentionedUser] = useState([]);
  const textareaRef = useRef(null); // Add this line
  // const location = useCurrentLocation({ enabled: true, searchPlace: false });
  const {
    userProfileInfo,
    refetchPhotosOfyou,
    refetchYourPhotos,
    refetchyourVideos,
    refetchvideosOfYou,
    selectedImgPost,
  } = useContext(MyProfileContext);
  const MAX_CHAR_COUNT = 250;
  const { companyPageDetail } = useSelector((state) => state.companyPage);
  const { userProfileInfo: userProfileInfoFromRedux } = useSelector((state) => state.userProfile) ?? {};
  const currentUserId = userProfileInfo?.id || userProfileInfoFromRedux?.id;
  const dispatch = useDispatch();
  const qc = useQueryClient();
  const { t } = useTranslator();
  const { allDegreeConnections, taggedListUsers } = useSelector(
    (state) => state.connection
  );
  const { companyPageList } = useSelector((state) => state.companyPage);
  const { feelingsAndActivities } = useSelector((s) => s.globalData) ?? {};

  const [agreeDisAgree, setAgreeDisAgree] = useState(true);
  const [canComment, setCanComment] = useState(true);

  const textInputRef = useRef();
  const formContainerRef = useRef();
  const fileInputRef = useRef();
  const newFileInputRef = useRef();
  const moreOptionsDisc = useDisclosure();
  const publishArticleDisc = useDisclosure();
  const videoProcessingDisc = useDisclosure();
  const socketRef = useRef(null);
  // const processingToastIdRef = useRef(null);
  const pendingPostIdRef = useRef(null);
  const isFromProfileFlagRef = useRef(false);
  const refetchAllRef = useRef(refetchAll);
  const refetchPhotosOfyouRef = useRef(refetchPhotosOfyou);
  const refetchYourPhotosRef = useRef(refetchYourPhotos);
  const refetchyourVideosRef = useRef(refetchyourVideos);
  const refetchvideosOfYouRef = useRef(refetchvideosOfYou);
  const userProfileInfoRef = useRef(userProfileInfo);
  const { onEvent } = useChatSocket();
  const accessToken = typeof window !== 'undefined' && localStorage.getItem(TOKEN_NAME);
  const {
    uploadFiles,
    uploadFile,
    cancelUpload,
    cancelAll,
    reset: resetMultipart,
    uploadState,
    uploadsList,
    isUploading,
    completedUploads,
    completedUploadIds,
  } = useMultipartUpload({
    uploadSource: 'post',
  });

  // Keep refs updated
  useEffect(() => {
    refetchAllRef.current = refetchAll;
    refetchPhotosOfyouRef.current = refetchPhotosOfyou;
    refetchYourPhotosRef.current = refetchYourPhotos;
    refetchyourVideosRef.current = refetchyourVideos;
    refetchvideosOfYouRef.current = refetchvideosOfYou;
    userProfileInfoRef.current = userProfileInfo;
  }, [refetchAll, refetchPhotosOfyou, refetchYourPhotos, refetchyourVideos, refetchvideosOfYou, userProfileInfo]);

  // Function to find the exceeding text
  const findExceedingText = (contentBlock, callback, contentState) => {
    const hasText = contentBlock.getKey();
    const text = contentBlock.getText();
    // console.log('text', hasText);
    if (text.length > 250) {
      callback(250, text.length);
    } else {
      setText?.(text);
    }
  };

  // Component to highlight the exceeding text
  const ExceedingText = (props) => {
    return (
      <span style={{ backgroundColor: '#FAD2D1', color: 'white' }}>
        {props.children}
      </span>
    );
  };
  const decorator = new CompositeDecorator([
    {
      strategy: findExceedingText,
      component: ExceedingText,
    },
  ]);

  const EditorDefaultState = EditorState.createEmpty(decorator);

  const [postEditorData, setPostEditorData] = useState(EditorDefaultState);
  const [shouldChange, setShouldChange] = useState(false);
  const [isFromProfileFlag, setisFromProfileFlag] = useState(false);
  useEffect(() => {
    if (post?.id) {
      setMode('edit');
      setExpanded(true);
      const { content, gif, media, tagged_users, feelings } = post ?? {};
      if (feelings?.id) {
        setFeeling(feelings);
      }
      if (content?.length) {
        setPostEditorData(
          EditorState.createWithContent(ContentState.createFromText(content))
        );
        setText(content);
      }
      if (tagged_users?.length) {
        setTagged(tagged_users);
      }
      if (gif?.length) {
        setGifs([
          {
            images: { original: { url: gif } },
          },
        ]);
      }
      if (media?.length) {
        const hasVid = media?.find?.((m) =>
          m?.file?.toLowerCase?.()?.includes?.('mp4')
        );
        if (hasVid) {
          setVideos([media?.[0]?.file]);
          setVideoObject(media?.[0]);
        } else {
          setImageObject(media);
          setImages(media?.map?.((m) => m?.file));
        }
      }
      setVisibility(post?.visibility);
      setReplyControl(post?.reply_control);
    }
  }, [post, post?.media?.[0]?.file]);


  // useEffect(() => {
  //   if (mediaUpdated) {
  //     console.log(mediaUpdated);
  //   }
  // }, [mediaUpdated]);

  useQuery({
    queryKey: ['company-pages', currentUserId],
    queryFn: () => dispatch(getAllCompanyPages()).then((res) => res.payload),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // enabled: isPostModalOpen || !!post?.id,
    enabled: (isPostModalOpen || !!post?.id) && !!currentUserId,
  });

  useEffect(() => {
    if (isPostModalOpen || post?.id) {
      dispatch(getTagUsersConnectionsData());
    }
  }, [isPostModalOpen, post?.id]);

  useEffect(() => {
    // This will run whenever post data changes (including when refetchAll brings new data)
    if (post?.id) {
      // Update canComment and agreeDisAgree based on the latest post data
      if (post.can_comment !== undefined) {
        setCanComment(post.can_comment);
      }
      
      if (post.can_agree !== undefined) {
        setAgreeDisAgree(post.can_agree);
      }
    }
  }, [post?.can_comment, post?.can_agree, post?.id]);

  // Define handler outside useEffect so it persists
  const handleMediaUploadedRef = useRef((data) => {
    // Check if this event is for our pending post
    const eventPostId = data?.post_id || data?.id || data?.post?.id;
    const pendingId = pendingPostIdRef.current;
    
    // Convert both to strings for comparison to handle type mismatches
    const eventIdStr = String(eventPostId);
    const pendingIdStr = String(pendingId);

    if (eventPostId && pendingId && eventIdStr === pendingIdStr) {
      // Close the processing toast if it exists
      // if (processingToastIdRef.current) {
      //   try {
      //     toast.close(processingToastIdRef.current);
      //     processingToastIdRef.current = null;
      //   } catch (e) {
      //     console.error('Error closing toast:', e);
      //   }
      // }

      // Show success toast
      toast({
        title: t('toast.postReady') || 'Your post is now ready',
        status: 'success',
        position: 'top',
        duration: 5000,
        isClosable: true,
      });

      // Refetch data using refs to avoid stale closures
      if (isFromProfileFlagRef.current) {
        const slug = userProfileInfoRef.current?.slug;
        if (slug) {
          refetchPhotosOfyouRef.current?.({ slug });
          refetchYourPhotosRef.current?.({ user: slug });
          refetchyourVideosRef.current?.({ user: slug });
          refetchvideosOfYouRef.current?.({ user: slug });
        }
      } else {
        refetchAllRef.current?.();
      }

      // Clear pending post ID
      pendingPostIdRef.current = null;
    }
  });

  // Initialize socket connection and set up media upload listener
  // useEffect(() => {
  //   if (!accessToken) return;
    
  //   // Create socket if it doesn't exist
  //   if (!socketRef.current) {
  //     socketRef.current = io(SOCKET_SERVER_URL, {
  //       extraHeaders: {
  //         token: `${accessToken}`,
  //       },
  //       reconnection: true,
  //       reconnectionDelay: 1000,
  //       reconnectionAttempts: 5,
  //     });

  //     socketRef.current.on('connect_error', (error) => {
  //       console.error('Socket connection error:', error);
  //     });
  //   }

  //   // Always ensure the listener is registered (even if socket already exists)
  //   const handleMediaUploaded = (data) => {
  //     handleMediaUploadedRef.current(data);
  //   };

  //   // Remove any existing listener first to avoid duplicates
  //   socketRef.current.off('media_uploaded', handleMediaUploaded);
    
  //   // Set up media upload listener
  //   socketRef.current.on('media_uploaded', handleMediaUploaded);

  //   return () => {
  //     if (socketRef.current) {
  //       socketRef.current.off('media_uploaded', handleMediaUploaded);
  //       // Don't disconnect - keep socket alive
  //     }
  //   };
  // }, [accessToken, toast, t]);

  useEffect(()=>{
    const handleMediaUploaded = (data) => {
      handleMediaUploadedRef.current(data);
    };

    const unsubscribe = onEvent("media_uploaded", handleMediaUploaded);

    return () => {
      // socketRef.current.off("media_uploaded", handleMediaUploaded);
      unsubscribe?.()
    }
  }, [onEvent])

  const addImage = (file) => {
    images.push(file);
    setMediaUpdated(true);
    setImages([...images]);
    setImageObject([...imageObject]);
  };

  const removeImage = (file, index) => {
    if (file === 'editMode') {
      setMediaUpdated(true);
    } else {
      const itemToRemove = images?.[index] || file;
      if (itemToRemove) {
        cancelUpload?.(itemToRemove);
      }
      const images_ = images?.filter((_, ind) => ind !== index);
      const imagesObject_ = imageObject?.filter((_, ind) => ind !== index);
      setMediaUpdated(true);
      setImages(images_);
      setImageObject(imagesObject_);
    }
  };

  const addGif = (file) => {
    gifs.push(file);
    setMediaUpdated(true);
    setGifs([...gifs]);
  };

  const removeGif = (file, index) => {
    const gifs_ = gifs?.filter((_, ind) => ind !== index);
    setGifs(gifs_);
  };

  const addVideo = (file) => {
    videos.push(file);
    setMediaUpdated(true);
    setVideos([...videos]);
  };

  const removeVideo = (file, index) => {
    const itemToRemove = videos?.[index] || file;
    if (itemToRemove) {
      cancelUpload?.(itemToRemove);
    }
    const videos_ = videos?.filter((_, ind) => ind !== index);
    setMediaUpdated(true);
    setVideos(videos_);
  };

  const companyPosting = (company) => {
    setCompanyId(company?.id);
  };

  const convertAndUploadThumbnail = async (url) => {
    if (!url) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = url;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (blob) {
          setThumbnailData(blob);
        }
      }, 'image/png');
    };
    img.onerror = () => {
      console.error('Failed to load image.');
    };
  };

  const { mutate: createPost, isLoading: createLoading } = useMutation(
    async ({ body, isFromProfile }) => {
      setisFromProfileFlag(isFromProfile);
      isFromProfileFlagRef.current = isFromProfile;
      return dispatch(newPost(body)).then(res => {
        if(res?.payload?.status_code >= 400){
          toast({
            title: "Error",
            duration: 3000,
            description: res?.payload?.message || res?.payload?.media?.[0],
            status: "error",
            isClosable: true,
            position: 'top'
          });
        }
        else if(res?.payload) {
          toast({
            title: res?.payload?.message || res?.payload?.media?.[0],
            duration: 3000,
            status: 'success',
            isClosable: true,
            position: 'top'
          })
        }
        return res;
      });
    },
    {
      onSuccess: (data) => {
      // Safe analytics tracking with proper error handling
      try {
        trackEvent(ANALYTICS_EVENTS.CreatePost, {
          userSlug: userProfileInfo?.slug || 'unknown',
          hasImages: Boolean(images?.length),
          hasVideos: Boolean(videos?.length),
          hasGifs: Boolean(gifs?.length),
          hasTaggedUsers: Boolean(tagged?.length),
          hasFeeling: Boolean(feeling),
          visibility: Number(visibility) || 0,
          isCompanyPost: Boolean(companyId),
          contentLength: typeof text === 'string' ? text.length : 0,
          postId: data?.payload?.post_id || data?.payload?.id || 'unknown',
          timestamp: new Date().toISOString(),
          // Add additional context that might be useful
          mediaCount: (images?.length || 0) + (videos?.length || 0) + (gifs?.length || 0),
          totalTaggedUsers: tagged?.length || 0
        });

      } catch (analyticsError) {
        console.error('Analytics tracking failed:', analyticsError);
      }
        onCreateSuccess?.(data, !!videos?.length);
        resetForm();
        setCloseModal(true);
        setMentionedUser([]); // Clear mentioned users after successful post creation
        
        // Store post ID for socket event handling
        const postId = data?.payload?.post_id || data?.payload?.id;
        if (videos?.length && postId) {
          pendingPostIdRef.current = postId;
        }

        // if (!videos.length) {
        //   refetchAll?.();
        // }
        refetchAll?.();
        
        // Show processing toast for videos (will be closed by socket event)
        // if (videos?.length && data?.payload) {
        //   const toastId = toast({
        //     position: 'top-right',
        //     render: ({ onClose }) => (
        //       <Box
        //         color="#5B5B5B"
        //         p={3}
        //         bg="white"
        //         boxShadow="0px 0px 10px 0px #00000033"
        //       >
        //         <HStack justifyContent="space-between">
        //           <Text flex="1">
        //             {t('toast.postProcessing') || "Your post is being processed. We'll let you know when it's ready to view."}
        //           </Text>
        //           <CloseButton onClick={() => {
        //             onClose();
        //             processingToastIdRef.current = null;
        //             pendingPostIdRef.current = null;
        //           }} />
        //         </HStack>
        //       </Box>
        //     ),
        //     duration: null, // No auto-close, will be closed by socket event
        //     isClosable: true,
        //   });
        //   processingToastIdRef.current = toastId;

        //   // Fallback: Close toast after 5 minutes if socket event doesn't arrive
        //   setTimeout(() => {
        //     if (processingToastIdRef.current && pendingPostIdRef.current === postId) {
        //       console.warn('Media upload timeout - closing processing toast as fallback');
        //       toast.close(processingToastIdRef.current);
        //       processingToastIdRef.current = null;
        //       pendingPostIdRef.current = null;
              
        //       // Show success toast anyway
        //       toast({
        //         title: t('toast.postReady') || 'Your post is now ready',
        //         status: 'success',
        //         position: 'top',
        //         duration: 2000,
        //         isClosable: true,
        //       });
        //     }
        //   }, 180000); // 3 minutes fallback
        // }
      },
      onError: (error) => {
        setCloseModal(true);
        console.log(error);
      },
    }
  );

  const createMultiPost = async (body) => {
    setMultiCreateLoading(true);
    let parent_post_id = null;
    for (const post of body) {
      const formData = new FormData();
      const postContent = post?.content ?? post?.text ?? '';
      // if posText is more than 300 characters then return
      if (postContent?.length > 300) {
        return;
      }
      if (post?.companyId) {
        formData.append('company_id', post?.companyId);
      }
      if (companyId) {
        formData.append('company_id', companyId);
      }
      formData.append('visibility', visibility || post?.visibility || 0);
      formData.append('reply_control', replyControl || post?.replyControl || 0);

      if (postContent?.length > 0) {
        formData.append('content', postContent);
      }

      if (post?.feeling?.id) {
        formData.append('feelings_id', post?.feeling?.id);
      }

      if (post?.tagged && post?.tagged?.length) {
        post?.tagged?.forEach?.((t) =>
          formData.append('tagged_user_ids', t?.id)
        );
      }

      if (mentionedUser && mentionedUser?.length) {
        formData.append('mention_users', JSON.stringify(mentionedUser));
      }

      if (post?.images?.length) {
        post?.images.forEach((image) => {
          // formData.append('media', image);
          if (typeof image === 'string' || (!completedUploadIds || !completedUploadIds.length)) {
            formData.append('media', image);
          }
        });
      }

      formData.append('can_comment', post?.canComment || canComment);
      formData.append('can_agree', post?.agreeDisAgree || canComment);

      if (post?.gifs?.length) {
        formData.append('gif', post?.gifs?.[0]?.images?.original?.url);
      }

      if (post?.videos?.length) {
        // formData.append('media', post?.videos?.[0]);
        if (typeof post?.videos?.[0] === 'string' || (!completedUploadIds || !completedUploadIds.length)) {
          formData.append('media', post?.videos?.[0]);
        }
      }
      // Resolve upload_ids only for files belonging to THIS post
      const postFiles = [
        ...(post?.images || []),
        ...(post?.videos || []),
      ].filter((f) => f instanceof File);

      if (postFiles.length > 0) {
        // Match each File to its completed uploadId via uploadState
        postFiles.forEach((file) => {
          const fileId = `${file.name}_${file.size}_${file.lastModified}`;
          const uploadId = uploadState?.[fileId]?.uploadId;
          if (uploadId) {
            formData.append('upload_ids', uploadId);
          }
        });
      }
      if (parent_post_id) {
        formData.append('parent_post', parent_post_id);
      }

      try {
        {/* setCloseModal(true); */}
        const response = await dispatch(newPost(formData));
        if (response?.payload?.post_id && parent_post_id === null) {
          parent_post_id = response?.payload?.post_id;
        }
      } catch (error) {
        {/* setCloseModal(true); */}
        console.log(error);
      }
    }
    setMultiCreateLoading(false);
    setMultiPost([]);
    resetForm();
    setCloseModal(true);
    refetchAll?.();
  };

  const editMultiPost = async (body) => {
    setMultiCreateLoading(true);
    let parent_post_id = null;
    for (const post of body) {
      const formData = new FormData();
      // if posText is more than 300 characters then return
      if (post?.content?.length > 300) {
        return;
      }
      if (post?.id && !parent_post_id) {
        parent_post_id = post?.id;
      }
      if (post?.companyId) {
        formData.append('company_id', post?.companyId);
      }
      formData.append('visibility', visibility || post?.visibility || 0);
      formData.append('reply_control', replyControl || post?.replyControl || 0);

      if (post?.content?.length > 0) {
        formData.append('content', post?.content);
      }

      if (post?.feeling?.id) {
        formData.append('feelings_id', post?.feeling?.id);
      }

      if (post?.tagged && post?.tagged?.length) {
        post?.tagged?.forEach?.((t) =>
          formData.append('tagged_user_ids', t?.id)
        );
      }

      if (mentionedUser && mentionedUser?.length) {
        formData.append('mention_users', JSON.stringify(mentionedUser));
      }

      // if (post?.media?.length) {
      //   post?.media?.forEach((media) => {
      //     formData.append('media', media);
      //   });
      // } else {
      //   formData.append('media', post?.media);
      // }

      if (post?.media?.length) {
        post?.media?.forEach((media) => {
          // Only append new File objects, skip existing URLs
          if (media instanceof File) {
            // formData.append('media', media);
            if (!completedUploadIds || !completedUploadIds.length) {
              formData.append('media', media);
            }
          } else if (typeof media === 'string') {
            formData.append('media', media);
          }
        });
      } else if (post?.media && post?.media instanceof File && (!completedUploadIds || !completedUploadIds.length)) {
        // formData.append('media', post?.media);
        formData.append('media', post?.media);
      }
      if (completedUploadIds && completedUploadIds.length > 0) {
        completedUploadIds.forEach((uId) => {
          formData.append('upload_ids', uId);
        });
      }

      formData.append(
        'can_comment',
        post?.canComment !== undefined ? post?.canComment : canComment
      );
      formData.append(
        'can_agree',
        post?.agreeDisAgree !== undefined ? post?.agreeDisAgree : canComment
      );

      if (post?.gifs?.length) {
        formData.append('gif', post?.gifs?.[0]?.images?.original?.url);
      } else if (post?.gif) {
        formData.append('gif', post?.gif);
      }

      if (post?.videos?.length) {
        formData.append('media', post?.media?.[0]);
      }
      if (parent_post_id) {
        formData.append('parent_post', parent_post_id);
      }

      const hasNewFiles = post?.media?.some(m => m instanceof File) || (post?.videos?.some(v => v instanceof File));
      const isPostMediaUpdated = !!(post.media_updated || hasNewFiles);

      formData.append('media_updated', isPostMediaUpdated);
      if (!isPostMediaUpdated) {
        formData.delete('media');
      }

      try {
        // setCloseModal(true);
        // await dispatch(updatePost({ formData: formData, id: post?.id }));
        // if (!!post?.id && mediaUpdated && mode !== 'edit') {
        if (!!post?.id) {
          await dispatch(updatePost({ formData: formData, id: post?.id }));
        } else if (!post?.id) {
          await dispatch(newPost(formData)).then(res => {
            if (res?.payload && !res?.error) {
              trackEvent(ANALYTICS_EVENTS.EditPost, userProfileInfo?.slug || 'anonymous');
            }
          });
        }
      } catch (error) {
        // setCloseModal(true);
        console.log(error);
      }
    }
    setMultiCreateLoading(false);
    setMultiPost([]);
    resetForm();
    setCloseModal(true);
    setMentionedUser([]); // Clear mentioned users after successful post creation
    refetchAll?.();
  };

  // const editMultiPost = async (body) => {
  //   console.log(body, 'posttttttttttttt');
  //   setMultiCreateLoading(true);
  //   let parent_post_id = null;
  //   for (const post of body) {
  //     const formData = new FormData();
  //     // if posText is more than 300 characters then return
  //     if (post?.content?.length > 300) {
  //       return;
  //     }
  //     if (post?.id && !parent_post_id) {
  //       parent_post_id = post?.id;
  //     }
  //     if (post?.companyId) {
  //       formData.append('company_id', post?.companyId);
  //     }
  //     formData.append('visibility', post?.visibility || 0);
  //     formData.append('reply_control', post?.replyControl || 0);

  //     if (post?.content?.length > 0) {
  //       formData.append('content', post?.content);
  //     }

  //     if (post?.feeling?.id) {
  //       formData.append('feelings_id', post?.feeling?.id);
  //     }

  //     if (post?.tagged && post?.tagged?.length) {
  //       post?.tagged?.forEach?.((t) =>
  //         formData.append('tagged_user_ids', t?.id)
  //       );
  //     }

  //     if (post?.media?.length) {
  //       post?.media?.forEach((media) => {
  //         if (typeof media != 'string') {
  //           formData.append('media', media);
  //         }
  //       });
  //     } else {
  //       typeof media != 'string' && formData.append('media', post?.media);
  //     }

  //     formData.append(
  //       'can_comment',
  //       post?.canComment !== undefined ? post?.canComment : true
  //     );
  //     formData.append(
  //       'can_agree',
  //       post?.agreeDisAgree !== undefined ? post?.agreeDisAgree : true
  //     );
  //     // if (post?.media?.length !== imageObject?.length && mediaUpdated) {
  //     //   formData.append('media_ids', imageObject);
  //     //   formData.delete('media');
  //     //   post?.media?.forEach((media) => {
  //     //     if (typeof media !== 'string') {
  //     //       formData.append('media_ids', media?.id);
  //     //     }
  //     //   });
  //     //   console.log(post, 'post?.media');
  //     // }

  //     // if (post?.media?.length) {
  //     //   post?.media?.forEach((media) => {
  //     //     // If the media is not a string (i.e., it's a new File), append it
  //     //     if (typeof media !== "string") {
  //     //       if (media instanceof File) {
  //     //         media?.id&&formData.append('media_ids', media?.id);
  //     //       }
  //     //     }
  //     //   });
  //     // }
  //     if (post?.gifs?.length) {
  //       formData.append(
  //         'gif',
  //         post?.gifs?.[0]?.images?.original?.url || post?.gifs[0]
  //       );
  //     formData.append('media_updated',mediaUpdated);

  //     }

  //     if (post?.videos?.length) {
  //       formData.append('media', post?.media?.[0]);
  //     formData.append('media_updated',mediaUpdated);
  //     }
  //     if (parent_post_id) {
  //       formData.append('parent_post', parent_post_id);
  //     }

  //     // formData.append('media_updated',mediaUpdated);
  //     if (!mediaUpdated ||(!post?.media?.length&& !post?.videos?.length)) {
  //       formData.delete('media');
  //       formData.delete('media_updated');
  //     }
  //     console.log(post, 'post?.media');
  //     try {
  //       setCloseModal(true);
  //       // await dispatch(updatePost({ formData: formData, id: post?.id }));
  //       if (!!post?.id) {
  //         await dispatch(updatePost({ formData: formData, id: post?.id }));
  //       } else if (!post?.id) {
  //         await dispatch(newPost(formData));
  //       }
  //     } catch (error) {
  //       setCloseModal(true);
  //       console.log(error);
  //     }
  //   }
  //   setMultiCreateLoading(false);
  //   setMultiPost([]);
  //   resetForm();
  //   refetchAll?.();
  // };

  const { mutate: quotePostMutate, isLoading: quotePostLoading } = useMutation(
    async (body) => {
      return dispatch(quotePost(body));
    },
    {
      onSuccess: (data) => {
        trackEvent(ANALYTICS_EVENTS.SharePost, userProfileInfo?.slug || 'anonymous');
        resetForm();
        refetchAll?.();
      },
      onError: (error) => {
        console.log(error);
      },
    }
  );

  const { mutate: editPost, isLoading: editLoading } = useMutation(
    async (body) => {
      return dispatch(updatePost({ id: post?.id, formData: body })).then(res => {
        toast({
          duration: 3000,
          title: res?.payload?.message,
          status: 'success',
          isClosable: true,
          position: 'top'
        });
      });
    },
    {
      onSuccess: (data) => {
        trackEvent(ANALYTICS_EVENTS.EditPost, userProfileInfo?.slug || 'anonymous');
        resetForm();
        setMentionedUser([]); // Clear mentioned users after successful post update
        refetchAll?.();
        onUpdateSuccess?.(data);
      },
      onError: (error) => {
        console.log(error);
      },
    }
  );

  const handleSubmit = async (lastPost, isFromProfile) => {
    const formData = new FormData();
    // if posText is more than 300 characters then return
    if (text?.length > 300) {
      return;
    }
    if (companyId) {
      formData.append('company_id', companyId);
    }
    formData.append('visibility', visibility);
    formData.append('reply_control', replyControl);

    if (text?.length > 0) {
      formData.append('content', text);
    }

    if (feeling?.id) {
      formData.append('feelings_id', feeling?.id);
    }

    if (tagged && tagged?.length) {
      tagged?.forEach?.((t) => formData.append('tagged_user_ids', t?.id));
    }

    if (mentionedUser && mentionedUser?.length) {
      formData.append('mention_users', JSON.stringify(mentionedUser));
    }

    if (images?.length) {
      images.forEach((image) => {
        // formData.append('media', image);
        if (typeof image === 'string' || (!completedUploadIds || !completedUploadIds.length)) {
          formData.append('media', image);
        }
      });
    }

    formData.append('can_comment', canComment);
    formData.append('can_agree', agreeDisAgree);

    // Handle both gifs array and gif string
    if (post?.gifs?.length) {
      formData.append('gif', post?.gifs?.[0]?.images?.original?.url);
    } else if (post?.gif) {
      formData.append('gif', post?.gif);
    }

    if (thumbnailData && gifs?.length) {
      formData.append(
        'video_thumbnail',
        thumbnailData,
        `${gifs?.[0]?.slug}.png`
      );
    }

    if(gifs?.length) {
      formData.append('gif', gifs?.[0]?.images?.original?.url);
    }

    if (videos?.length) {
      // formData.append('media', videos?.[0]);
      if (typeof videos?.[0] === 'string' || (!completedUploadIds || !completedUploadIds.length)) {
        formData.append('media', videos?.[0]);
      }
      
      // Only generate thumbnail for new video files, not existing URLs
      if (videos?.[0] instanceof File) {
        const thumbnail = await generateVideoThumbnail(videos?.[0]);
        let fileName = videos?.[0]?.name;
        if (thumbnail) {
          formData.append(
            'video_thumbnail',
            thumbnail,
            `${fileName?.substring(0, fileName.lastIndexOf('.'))}.png`
          );
        }
      }
      // For existing videos (URLs), you might want to skip thumbnail generation
      // or handle it differently based on your requirements
    }
    if (completedUploadIds && completedUploadIds.length > 0) {
      completedUploadIds.forEach((uId) => {
        formData.append('upload_ids', uId);
      });
    }
    // console.log('formData', videos, JSON.stringify(Object.fromEntries(formData)));
    //MARK: - Create Post
    if (mode === 'create' && multiPost.length === 0) {
      createPost({ body: formData, isFromProfile });
    } else if (mode === 'create' && multiPost.length > 0) {
      createMultiPost([...multiPost, lastPost]);
    } else if (mode === 'edit' && multiPost.length <= 1) {
      formData.append('media_updated', mediaUpdated);
      if (!mediaUpdated) {
        formData.delete('media');
      }
      editPost(formData);
    } else if (mode === 'edit' && multiPost.length > 1) {
      editMultiPost(multiPost);
    }
  };

  const generateVideoThumbnail = (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const video = document.createElement('video');

      video.autoplay = true;
      video.muted = true;
      
      // Check if file is a File object or a URL string
      if (file instanceof File) {
        video.src = URL.createObjectURL(file);
      } else if (typeof file === 'string') {
        video.src = file;
      } else {
        // Handle other cases or reject the promise
        resolve(null);
        return;
      }

      video.onloadeddata = () => {
        let ctx = canvas.getContext('2d');

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
        video.pause();
        
        // Clean up object URL if it was created
        if (file instanceof File) {
          URL.revokeObjectURL(video.src);
        }
        
        return resolve(b64toBlob(canvas.toDataURL('image/png')));
      };

      video.onerror = () => {
        // Clean up object URL if it was created
        if (file instanceof File) {
          URL.revokeObjectURL(video.src);
        }
        resolve(null);
      };
    });
  };

  const b64toBlob = (dataURI) => {
    var byteString = atob(dataURI.split(',')[1]);
    var ab = new ArrayBuffer(byteString.length);
    var ia = new Uint8Array(ab);

    for (var i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: 'image/jpeg' });
  };

  const submitPost = (lastPost, isFromProfile) => {

    handleSubmit(lastPost, isFromProfile);
  };

  const submitQuotePost = (selectedPostingProfile=null) => {
    const payload = {
      id: post?.id,
      content: text,
    };

    if (selectedPostingProfile?.company_type) {
      payload.company_id = selectedPostingProfile?.id;
    } 

    quotePostMutate(payload);
  };

  const setAgreeDisAgreeVisibility = (e) => {
    setAgreeDisAgree(e);
  };

  const setCanCommentVisibility = (e) => {
    setCanComment(e);
  };

  // const onReactionClick = () => {
  //   setReactionModalOpen(true);
  // }

  const resetForm = (keepFeeling = false, keepMultipart = false) => {
    setImages([]);
    setImageObject([]);
    setGifs([]);
    setVideos([]);
    // Keep tagged users when adding to queue in create mode
    if (!keepFeeling) {
      setTagged([]);
    }
    setText('');
    setReplyControl(0);
    setVisibility(0);
    setCloseModal(false);
    setShouldChange(true);
    setPostEditorData(EditorDefaultState);
    // Only reset feeling if keepFeeling is false (e.g., when closing modal, not when adding to queue)
    if (!keepFeeling) {
      setFeeling(null);
    }
    if (mode === 'edit') {
      setMediaUpdated(false);
    }
    if (!keepMultipart) {
      resetMultipart?.();
    }
  };

  const handleMediaFilesChange = (e) => {
    const { files } = e?.target ?? {};
    const filesArr = Array.from(files || []);
    if (filesArr.length > 0) {
      uploadFiles(filesArr, 'post');
    }
    const video = filesArr?.find?.((f) => f.type?.startsWith('video/'));
    if (video) {
      setVideoObject(video);
      setVideos([video]);
      setImages([]);
      setImageObject([]);
    } else {
      const endIndex = 5 - images.length;
      setImages([
        ...(images ?? []),
        ...filesArr.slice(0, Math.min(filesArr.length, endIndex)),
      ]);
      setImageObject([
        ...(imageObject ?? []),
        ...filesArr.slice(0, Math.min(filesArr.length, endIndex)),
      ]);
      setVideos([]);
      setVideoObject([]);
    }
    setMediaUpdated?.(true);
    setTimeout(() => {
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      } else if (newFileInputRef.current) {
        newFileInputRef.current.value = null;
      }
    }, 100);
  };

  const hasContent = useMemo(() => {
    return (
      text?.length > 0 ||
      images?.length > 0 ||
      videos?.length > 0 ||
      gifs?.length > 0
    );
  }, [text, images, videos, gifs]);

  // useEffect(() => {
  //   if (mode === 'edit' && multiPost && multiPost.length > 0) {
  //     // Sync current form state with the multiPost array
  //     const updatedMultiPost = multiPost.map((item, i) => {
  //       // Only update the currently active post (you might need to track which one is active)
  //       return {
  //         ...item,
  //         content: item.content || text,
  //         media: item.media || (images.length ? images : videos.length ? videos : []),
  //         gif: item.gif || (gifs.length ? gifs[0]?.images?.original?.url : null),
  //         tagged: item.tagged || tagged,
  //         feelings: item.feelings || feeling,
  //         visibility: item.visibility !== undefined ? item.visibility : visibility
  //       };
  //     });
      
  //     // Only update if there are actual changes to prevent infinite loops
  //     if (JSON.stringify(updatedMultiPost) !== JSON.stringify(multiPost)) {
  //       setMultiPost(updatedMultiPost);
  //     }
  //   }
  // }, [images, videos, gifs, tagged, feeling, visibility, text, mode]);

  useEffect(() => {
    if (mode === 'edit' && multiPost && multiPost.length > 0) {
      const updatedMultiPost = multiPost.map((item) => {
        return {
          ...item,
          // content: text ?? item.content ?? null,
          content: item.content ?? null,
          media: item.media ?? (images?.length ? images : (videos?.length ? videos : [])),
          // DO NOT fallback to global `gifs` here — keep item.gif as-is or null
          gif: item.gif ?? null,
          tagged: item.tagged && item?.tagged?.length>0 ? item.tagged : tagged,
          feelings: item.feelings ?? feeling,
          visibility: item.visibility !== undefined ? item.visibility : visibility
        };
      });

      if (JSON.stringify(updatedMultiPost) !== JSON.stringify(multiPost)) {
        setMultiPost(updatedMultiPost);
      }
    }
  }, [images, videos, gifs, tagged, feeling, visibility, text, mode]);

  // Propagate feeling to all posts in multiPost when feeling changes
  useEffect(() => {
    if (feeling && multiPost && multiPost.length > 0) {
      const updatedMultiPost = multiPost.map((item) => ({
        ...item,
        feeling: feeling,
      }));

      if (JSON.stringify(updatedMultiPost) !== JSON.stringify(multiPost)) {
        setMultiPost(updatedMultiPost);
      }
    }
  }, [feeling]);

  // Propagate tagged users to all posts in multiPost when tagged changes
  useEffect(() => {
    if (tagged && tagged.length > 0 && multiPost && multiPost.length > 0) {
      const updatedMultiPost = multiPost.map((item) => ({
        ...item,
        tagged: tagged,
      }));

      if (JSON.stringify(updatedMultiPost) !== JSON.stringify(multiPost)) {
        setMultiPost(updatedMultiPost);
      }
    }
  }, [tagged]);

  return {
    text,
    setText,
    images,
    setImages,
    videos,
    setVideos,
    gifs,
    setGifs,
    textInputRef,
    addImage,
    removeImage,
    addVideo,
    removeVideo,
    addGif,
    removeGif,
    replyControl,
    closeModal,
    setReplyControl,
    setCloseModal,
    visibility,
    setVisibility,
    textMaxLength: 250,
    submitPost,
    isLoading: createLoading || editLoading || multiCreateLoading,
    expanded,
    setExpanded,
    formContainerRef,
    hasContent,
    mode,
    setMode,
    setMediaUpdated,
    mediaUpdated,
    quotePost: submitQuotePost,
    quotePostLoading,
    companyPosting,
    moreOptionsActiveTab,
    setMoreOptionsActiveTab,
    moreOptionsDisc,
    feeling,
    setFeeling,
    allDegreeConnections,
    tagged,
    setTagged,
    feelingsAndActivities,
    handleMediaFilesChange,
    fileInputRef,
    newFileInputRef,
    publishArticleDisc,
    publishingData,
    setPublishingData,
    agreeDisAgree,
    setAgreeDisAgreeVisibility,
    canComment,
    setCanCommentVisibility,
    // onClose,
    // onReactionClick
    multiPost,
    setMultiPost,
    setImageObject,
    imageObject,
    resetForm,
    isDisable,
    isRemoved,
    setIsRemoved,
    setIsDisable,
    postEditorData,
    setPostEditorData,
    EditorDefaultState,
    isCompany,
    setIsCompany,
    isReactionMenuOpen,
    setIsReactionMenuOpen,
    isControlMenuOpen,
    setIsControlMenuOpen,
    setShouldChange,
    shouldChange,
    thumbnailData,
    setThumbnailData,
    convertAndUploadThumbnail,
    openMoreOptions,
    setOpenMoreOptions,
    setEmojis,
    emojis,
    taggedListUsers,
    textareaRef, // Add this line
    caretPosition,
    setCaretPosition,
    mentionedUser,
    setMentionedUser,
    fromProfilePhotoVideo,
    uploadFiles,
    uploadFile,
    cancelUpload,
    cancelAll,
    resetMultipart,
    uploadState,
    uploadsList,
    isUploading,
    completedUploads,
    completedUploadIds,
  };
};
